import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  styled,
  useTheme,
  Divider,
} from "@mui/material";
import CalendarIcon from "@icons/myicons/calendar.svg?react";
import { QuickRangeStrip } from "./QuickRangeStrip";
import { DatePopover } from "./DatePopover";
import {
  useDateRangePicker,
  type UseDateRangePickerParams,
} from "./useDateRangePicker";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(4),
  gap: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[8],
  maxWidth: "380px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(3),
  },
}));

export interface DateRangePickerProps extends UseDateRangePickerParams {
  className?: string;
}

export const DateRangePicker = ({
  className,
  ...hookParams
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    innerFrom,
    innerTo,
    activePreset,
    inputFrom,
    inputTo,
    setInputFrom,
    setInputTo,
    handleRelativePreset,
    handleYearPreset,
    commitInputChange,
    handleFieldIconClick,
    handleApplyClick,
    handleResetClick,
    ...rest
  } = useDateRangePicker(hookParams as UseDateRangePickerParams);

  return (
    <>
      <Container className={className}>
        <Box flex={1} minWidth={0}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={5}
          >
            <Typography variant="body1">
              {t("dateRangePicker.title")}
            </Typography>
          </Box>

          <QuickRangeStrip
            onRelativePreset={handleRelativePreset}
            onYearPreset={handleYearPreset}
            activeKey={activePreset}
          />

          <Box display="flex" gap={2} mt={3}>
            <TextField
              fullWidth
              size="small"
              label={t("dateRangePicker.startLabel")}
              value={inputFrom}
              placeholder="dd.mm.yyyy"
              onChange={(event) => setInputFrom(event.target.value)}
              onBlur={() => commitInputChange("from")}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitInputChange("from");
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleFieldIconClick("from")}
                      >
                        <CalendarIcon
                          width={20}
                          height={20}
                          color={theme.palette.icon.primary}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              size="small"
              value={inputTo}
              label={t("dateRangePicker.endLabel")}
              placeholder="dd.mm.yyyy"
              onChange={(event) => setInputTo(event.target.value)}
              onBlur={() => commitInputChange("to")}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitInputChange("to");
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleFieldIconClick("to")}
                      >
                        <CalendarIcon
                          width={20}
                          height={20}
                          color={theme.palette.icon.primary}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Divider sx={{ mt: 4 }} />

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              variant="tertiary"
              color="inherit"
              disabled={!innerFrom && !innerTo}
              onClick={handleResetClick}
            >
              {t("dateRangePicker.resetLabel", "Zurücksetzen")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!innerFrom || !innerTo}
              onClick={handleApplyClick}
            >
              {t("dateRangePicker.applyLabel", "Anwenden")}
            </Button>
          </Box>
        </Box>
      </Container>

      <DatePopover innerFrom={innerFrom} innerTo={innerTo} {...rest} />
    </>
  );
};
